import { errorEnvelopeSchema, successEnvelopeSchema } from '@canopee/contracts';
import { z } from 'zod';

export class ApiClientError extends Error {
  readonly code: string;
  readonly details: Readonly<Record<string, unknown>>;
  readonly status: number;

  constructor(
    code: string,
    message: string,
    status: number,
    details: Readonly<Record<string, unknown>> = {},
  ) {
    super(message);
    this.name = 'ApiClientError';
    this.code = code;
    this.details = details;
    this.status = status;
  }
}

export interface ApiRequestOptions {
  readonly method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  readonly body?: unknown;
  readonly signal?: AbortSignal;
  readonly headers?: Readonly<Record<string, string>>;
}

export interface ApiFormDataRequestOptions {
  readonly method?: 'POST' | 'PUT' | 'PATCH';
  readonly signal?: AbortSignal;
}

export async function apiRequest<TSchema extends z.ZodType>(
  path: string,
  dataSchema: TSchema,
  options: ApiRequestOptions = {},
): Promise<z.output<TSchema>> {
  const request: RequestInit = {
    method: options.method ?? 'GET',
    credentials: 'same-origin',
  };
  const headers = new Headers(options.headers);
  if (options.body !== undefined) {
    headers.set('content-type', 'application/json');
    request.body = JSON.stringify(options.body);
  }
  request.headers = headers;
  if (options.signal !== undefined) request.signal = options.signal;

  return parseApiResponse(await performRequest(path, request), dataSchema);
}

export async function apiFormDataRequest<TSchema extends z.ZodType>(
  path: string,
  dataSchema: TSchema,
  body: FormData,
  options: ApiFormDataRequestOptions = {},
): Promise<z.output<TSchema>> {
  const request: RequestInit = {
    method: options.method ?? 'POST',
    credentials: 'same-origin',
    body,
  };
  if (options.signal !== undefined) request.signal = options.signal;
  return parseApiResponse(await performRequest(path, request), dataSchema);
}

async function performRequest(path: string, request: RequestInit): Promise<Response> {
  try {
    return await fetch(path, request);
  } catch {
    throw new ApiClientError(
      'NETWORK_ERROR',
      'Le serveur est injoignable. Vérifiez votre connexion puis réessayez.',
      0,
    );
  }
}

async function parseApiResponse<TSchema extends z.ZodType>(
  response: Response,
  dataSchema: TSchema,
): Promise<z.output<TSchema>> {
  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const parsedError = errorEnvelopeSchema.safeParse(payload);
    if (parsedError.success) {
      throw new ApiClientError(
        parsedError.data.error.code,
        parsedError.data.error.message,
        response.status,
        parsedError.data.error.details,
      );
    }
    throw new ApiClientError(
      'INVALID_API_RESPONSE',
      'Le serveur a renvoyé une erreur non conforme.',
      response.status,
    );
  }

  const parsedSuccess = successEnvelopeSchema(z.unknown()).safeParse(payload);
  if (!parsedSuccess.success) {
    throw new ApiClientError(
      'INVALID_API_RESPONSE',
      'La réponse du serveur ne respecte pas le contrat.',
      response.status,
      { issues: parsedSuccess.error.issues },
    );
  }
  return dataSchema.parse(parsedSuccess.data.data);
}
